import React from 'react';
import { styled } from '@linaria/react';
import { SizesType } from 'types/Sizes';
import { ColorPath } from 'types/Theme';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { cssTheme } from '../../../../themes';
import { linariaMq } from '../../../../utils/breakpoints';
import { Badge } from '../../Badge';
import { Icon } from '../../Icon';
import { Tag } from '../../Tag';
export type InputBaseSize = Extract<SizesType, 'sm' | 'md' | 'lg'>;
export enum TextTransform {
  uppercase = 'uppercase',
  capitalize = 'capitalize',
  lowercase = 'lowercase',
  none = 'none',
}
export enum InputTextType {
  text = 'text',
  password = 'password',
  email = 'email',
  tel = 'tel',
  search = 'search',
  textarea = 'textarea',
}
export const CLEAR_BUTTON_MIN_WIDTH = 14;
interface StyleProps {
  showInvalid?: boolean;
  showValid?: boolean;
  isResizable?: boolean;
  isRounded?: boolean;
  sizeVariant?: InputBaseSize;
  textTransformType?: TextTransform;
  withBorder?: boolean;
  withStartIcon?: boolean;
  withEndIcon?: boolean;
}
interface ValidationBadgeProps {
  isVerified?: boolean;
  showInvalid?: boolean;
  showValid?: boolean;
}
interface IconsWrapperProps {
  isRounded?: boolean;
  sizeVariant?: InputBaseSize;
}
interface WithInsideLabelIconsWrapperProps {
  sizeVariant: InputBaseSize;
  withRightShifting?: boolean;
}
interface CaratWrapperProps {
  isOpen: boolean;
  disabled?: boolean;
  sizeVariant: InputBaseSize;
}
interface IconSizeMapItem {
  padding: number;
  rightIconWidth: number;
  height: number;
}
export const SM_INPUT_HEIGHT = 30;
export const MD_INPUT_HEIGHT = 42;
export const LG_INPUT_HEIGHT = 48;
export const MD_INPUT_WITH_INSIDE_LABEL_HEIGHT = 48;
export const LG_INPUT_WITH_INSIDE_LABEL_HEIGHT = 56;
export const iconsSizeMap: Record<InputBaseSize, IconSizeMapItem> = {
  sm: {
    padding: 8,
    rightIconWidth: 30,
    height: SM_INPUT_HEIGHT
  },
  md: {
    padding: 12,
    rightIconWidth: 40,
    height: MD_INPUT_HEIGHT
  },
  lg: {
    padding: 12,
    rightIconWidth: 44,
    height: LG_INPUT_HEIGHT
  }
};
const iconsWithInsideLabelSizeMap: Record<InputBaseSize, IconSizeMapItem> = {
  sm: {
    padding: 12,
    rightIconWidth: 40,
    height: MD_INPUT_WITH_INSIDE_LABEL_HEIGHT
  },
  md: {
    padding: 12,
    rightIconWidth: 40,
    height: MD_INPUT_WITH_INSIDE_LABEL_HEIGHT
  },
  lg: {
    padding: 12,
    rightIconWidth: 44,
    height: LG_INPUT_WITH_INSIDE_LABEL_HEIGHT
  }
};
export const FS_PRIVACY_MASK_CLASS = 'fs-mask';
export const ICON_SIZE = 20;
const VALIDATION_ICON_SIZE = 16;
export const getBaseInputIconPaddingStyles = ({
  sizeVariant = 'lg',
  isRounded = false,
  withStartIcon = false,
  withEndIcon = true,
  showInvalid = false,
  showValid = false
}: StyleProps) => {
  const gap = cssTheme.layout.spacing.s250;
  const roundedPadding = isRounded ? cssTheme.layout.spacing.s200 : '0px';
  const size = iconsSizeMap[sizeVariant];
  const withValidationIcon = showInvalid || showValid;
  const paddingLeft = `calc(${size.padding}px + ${withStartIcon ? `${ICON_SIZE}px + ${gap}` : '0px'})`;
  const paddingRight = `calc(${roundedPadding} + ${withEndIcon ? `${size.rightIconWidth}px` : '0px'} + ${withValidationIcon ? `${VALIDATION_ICON_SIZE}px + ${gap}` : '0px'})`;
  return {
    paddingLeft,
    paddingRight
  };
};
export const getBaseInsideLabelIconPaddingStyles = ({
  sizeVariant = 'lg',
  withStartIcon = false,
  withEndIcon = true,
  showInvalid = false,
  showValid = false
}: StyleProps) => {
  const gap = cssTheme.layout.spacing.s250;
  const size = iconsWithInsideLabelSizeMap[sizeVariant];
  const withValidationIcon = showInvalid || showValid;
  const paddingLeft = `calc(${size.padding}px + ${withStartIcon ? `${ICON_SIZE}px + ${gap}` : '0px'})`;
  const paddingRight = `calc(${withEndIcon ? `${size.rightIconWidth}px` : '0px'} + ${withValidationIcon ? `${VALIDATION_ICON_SIZE}px + ${gap}` : '0px'})`;
  return {
    paddingLeft,
    paddingRight
  };
};
const _exp = /*#__PURE__*/() => ({
  sizeVariant = 'lg'
}) => ({
  sm: {
    padding: 12,
    rightIconWidth: 40,
    height: 48
  },
  md: {
    padding: 12,
    rightIconWidth: 40,
    height: 48
  },
  lg: {
    padding: 12,
    rightIconWidth: 44,
    height: 56
  }
})[sizeVariant].height;
const BaseInsideLabelIconsWrapper = /*#__PURE__*/styled('div')({
  name: "BaseInsideLabelIconsWrapper",
  class: "bay35h4",
  propsAsIs: false,
  vars: {
    "bay35h4-0": [_exp(), "px"]
  }
});
const _exp2 = /*#__PURE__*/() => BaseInsideLabelIconsWrapper;
const _exp3 = /*#__PURE__*/() => ({
  sizeVariant
}) => ({
  sm: {
    padding: 12,
    rightIconWidth: 40,
    height: 48
  },
  md: {
    padding: 12,
    rightIconWidth: 40,
    height: 48
  },
  lg: {
    padding: 12,
    rightIconWidth: 44,
    height: 56
  }
})[sizeVariant].padding;
export const WithInsideLabelStartIconsWrapper = /*#__PURE__*/styled(_exp2())({
  name: "WithInsideLabelStartIconsWrapper",
  class: "waj995a",
  propsAsIs: true,
  vars: {
    "waj995a-0": [_exp3(), "px"]
  }
});
const _exp4 = /*#__PURE__*/() => BaseInsideLabelIconsWrapper;
const _exp5 = /*#__PURE__*/() => ({
  withRightShifting
}) => withRightShifting ? cssTheme.layout.spacing.s500 : 0;
export const WithInsideLabelEndIconsWrapper = /*#__PURE__*/styled(_exp4())({
  name: "WithInsideLabelEndIconsWrapper",
  class: "w18vr5r",
  propsAsIs: true,
  vars: {
    "w18vr5r-0": [_exp5()]
  }
});
const _exp7 = /*#__PURE__*/() => ({
  sizeVariant = 'lg'
}) => ({
  sm: {
    padding: 8,
    rightIconWidth: 30,
    height: 30
  },
  md: {
    padding: 12,
    rightIconWidth: 40,
    height: 42
  },
  lg: {
    padding: 12,
    rightIconWidth: 44,
    height: 48
  }
})[sizeVariant].height;
const BaseIconsWrapper = /*#__PURE__*/styled('div')({
  name: "BaseIconsWrapper",
  class: "bkfq80h",
  propsAsIs: false,
  vars: {
    "bkfq80h-0": [_exp7(), "px"]
  }
});
const _exp8 = /*#__PURE__*/() => BaseIconsWrapper;
const _exp9 = /*#__PURE__*/() => ({
  sizeVariant = 'lg'
}) => ({
  sm: {
    padding: 8,
    rightIconWidth: 30,
    height: 30
  },
  md: {
    padding: 12,
    rightIconWidth: 40,
    height: 42
  },
  lg: {
    padding: 12,
    rightIconWidth: 44,
    height: 48
  }
})[sizeVariant].padding;
export const StartIconsWrapper = /*#__PURE__*/styled(_exp8())({
  name: "StartIconsWrapper",
  class: "sfiqq7y",
  propsAsIs: true,
  vars: {
    "sfiqq7y-0": [_exp9(), "px"]
  }
});
const _exp10 = /*#__PURE__*/() => BaseIconsWrapper;
const _exp11 = /*#__PURE__*/() => ({
  isRounded
}) => isRounded ? cssTheme.layout.spacing.s200 : 0;
export const EndIconsWrapper = /*#__PURE__*/styled(_exp10())({
  name: "EndIconsWrapper",
  class: "e18fajvs",
  propsAsIs: true,
  vars: {
    "e18fajvs-0": [_exp11()]
  }
});
export const focusShadowStyles = `
  box-shadow: 0px 0px 0px 1px ${cssTheme.colors.input.default.focus.border};
`;
export const baseInputShadowStyles = `
  &:focus {
    ${focusShadowStyles};
  }
`;
export const baseInputRadiusStyles = `
  border-radius: ${cssTheme.layout.radius.lg};
  &--size-sm {
    border-radius: ${cssTheme.layout.radius.sm};
  }
  &.isRounded {
    border-radius: ${cssTheme.layout.radius.rounded};
  }
`;
export const baseInputBaseLayoutStyles = `
  display: inline-flex;
  align-items: center;
  position: relative;
  outline: none;
  width: 100%;
  gap: ${cssTheme.layout.spacing.s250};
  border: 1px solid ${cssTheme.colors.input.default.border};
  color: ${cssTheme.colors.input.default.text};
  background: ${cssTheme.colors.input.default.background};
  -webkit-appearance: none;

  ::-webkit-search-cancel-button {
    display: none;
  }

  &::placeholder {
    color: ${cssTheme.colors.input.default.placeholder};
    text-transform: none;
  }
`;
export const defaultBorderStyles = `
  border-color: ${cssTheme.colors.input.default.border};
`;
const activeBorderStyles = `
  border-color: ${cssTheme.colors.input.default.active.border};
`;
export const focusBorderStyles = `
  border-color: ${cssTheme.colors.input.default.focus.border};
`;
const hoverBorderStyles = `
  border-color: ${cssTheme.colors.input.default.hover.border};
`;
const invalidBorderStyles = `
  border-color: ${cssTheme.colors.input.error.border};
  z-index: 1;
`;
export const baseInputBorderStyles = `
  ${defaultBorderStyles}

  ${linariaMq.desktopXs} {
    &:hover {
      ${hoverBorderStyles}
    }
  }

  &:active {
    ${activeBorderStyles}
  }

  &:focus {
    ${focusBorderStyles}
  }
`;
export const baseInputBackgroundStyles = `
  ${linariaMq.desktopXs} {
    &:hover {
      background: ${cssTheme.colors.input.default.hover.background};
    }
  }

  &:active {
    background: ${cssTheme.colors.input.default.active.background};
  }
`;
export const baseInputTypographyStyles = "bdaxcb4";
export const baseInputSizeStyles = "bvrhefk";
export const baseInputInsideLabelSizeStyles = "b1kujof3";
export const baseInputDisabledStyles = `
  &:disabled {
    color: ${cssTheme.colors.input.disabled.text};
    border-color: ${cssTheme.colors.input.disabled.border};
    background-color: ${cssTheme.colors.input.disabled.background};
  }
`;
export const InputWrapper = /*#__PURE__*/styled('div')({
  name: "InputWrapper",
  class: "ibywhvx",
  propsAsIs: false
});
export const InputWithInsideLabelWrapper = /*#__PURE__*/styled('div')({
  name: "InputWithInsideLabelWrapper",
  class: "i4bob1q",
  propsAsIs: false
});
export const baseInputInvalidStateStyles = `
  &.isInvalid {
    ${invalidBorderStyles}
    &:active {
      ${invalidBorderStyles}
    }
    &:focus {
      ${focusBorderStyles};
    }
    ${linariaMq.desktopXs} {
      &:hover {
        ${invalidBorderStyles}
      }
    }
  }
`;
const _exp32 = /*#__PURE__*/() => ({
  showInvalid
}) => showInvalid ? cssTheme.colors.input.error.caption : cssTheme.colors.input.default.caption;
export const CaptionWrapper = /*#__PURE__*/styled('div')({
  name: "CaptionWrapper",
  class: "c1wjfldx",
  propsAsIs: false,
  vars: {
    "c1wjfldx-0": [_exp32()]
  }
});
const VerifiedTag = () => <Tag variant="success" size="sm" iconStart={<Icon name="Checkmark" size="xs" />}>
    <FormattedMessageWrapper id="inputText.verified" defaultMessage="Verified" />
  </Tag>;
export const ValidationBadge = ({
  isVerified = false,
  showInvalid,
  showValid
}: ValidationBadgeProps) => {
  if (!showInvalid && !showValid) return null;
  const iconsName = showValid ? 'Checkmark' : 'WarningMark';
  const variant = showValid ? 'success' : 'danger';
  return isVerified ? <VerifiedTag /> : <Badge iconName={iconsName} size="xs" variant={variant} />;
};
export const getClassName = (isPrivate?: boolean) => isPrivate ? FS_PRIVACY_MASK_CLASS : undefined;
export const ClearButtonWrapper = /*#__PURE__*/styled('div')({
  name: "ClearButtonWrapper",
  class: "cux3q0b",
  propsAsIs: false
});
const _exp34 = /*#__PURE__*/() => ({
  isOpen
}) => isOpen ? 'rotateZ(0)' : 'rotateZ(180deg)';
const _exp35 = /*#__PURE__*/() => ({
  disabled
}) => disabled ? cssTheme.colors.input.disabled.carat : cssTheme.colors.input.default.carat;
const _exp36 = /*#__PURE__*/() => ({
  sizeVariant
}) => `${{
  sm: {
    padding: 8,
    rightIconWidth: 30,
    height: 30
  },
  md: {
    padding: 12,
    rightIconWidth: 40,
    height: 42
  },
  lg: {
    padding: 12,
    rightIconWidth: 44,
    height: 48
  }
}[sizeVariant].rightIconWidth}px`;
export const CaratWrapper = /*#__PURE__*/styled('div')({
  name: "CaratWrapper",
  class: "c36hq8h",
  propsAsIs: false,
  vars: {
    "c36hq8h-0": [_exp34()],
    "c36hq8h-1": [_exp35()],
    "c36hq8h-2": [_exp36()]
  }
});
export const baseInputAppearanceNoneStyles = `
  appearance: none;
`;
interface GetInsideLabelTextColorPathParams {
  isFocused?: boolean;
  isDisabled?: boolean;
  hasError?: boolean;
  hasValue?: boolean;
  defaultOverride?: ColorPath;
}

/**
 * Get the color path for the inside label text based on the input state
 * @param isFocused - is the input focused
 * @param isDisabled - is the input disabled
 * @param hasError - does the input have an error
 * @param hasValue - does the input have a value
 * @param defaultOverride - override the default color path
 * @returns the color path for the inside label text
 */
export const getInsideLabelTextColorPath = ({
  isFocused,
  isDisabled,
  hasError,
  hasValue,
  defaultOverride
}: GetInsideLabelTextColorPathParams): ColorPath => {
  if (isFocused) {
    return 'input.default.text';
  }
  if (isDisabled) {
    return 'input.disabled.text';
  }
  if (hasError) {
    return 'input.error.caption';
  }
  if (hasValue) {
    return 'input.default.caption';
  }
  return defaultOverride || 'input.default.placeholder';
};

require("./index.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");